import BackgroundGradient from "%/dynamic-component-editor/options/background-gradient";
import { suppressesNavigation } from "%/dynamic-component-editor/helpers/builder-navigation-guard";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BoxShadow from "%/dynamic-component-editor/options/box-shadow";
import ClickAction from "%/dynamic-component-editor/options/click-action";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import Padding, {
  PADDING_DEFAULT_EMPTY_VALUE,
} from "%/dynamic-component-editor/options/padding";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import Margins, {
  MARGIN_DEFAULT_EMPTY_VALUE,
} from "%/dynamic-component-editor/options/margins";
import ButtonType from "%/dynamic-component-editor/options/button-type";
import ButtonDisabled from "%/dynamic-component-editor/options/button-disabled";
import Icon from "%/dynamic-component-editor/options/icon";
import IconPosition from "%/dynamic-component-editor/options/icon-position";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import ButtonSize from "%/dynamic-component-editor/options/button-size";
import ButtonIconSize from "%/dynamic-component-editor/options/button-icon-size";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import LinkType from "%/dynamic-component-editor/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import { isBuilderQuiet, pageLinkValueFor } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-page-target";
import {
  hasUnfilledPlaceholder,
  resolveContextEntityHref,
  resolveUriVarsHref,
} from "%/dynamic-component-editor/standard-elements/click-actions/resolve-context-href";
import ButtonBranding from "%/dynamic-component-editor/classes/branding/button-branding";
import ButtonBorderColor from "%/dynamic-component-editor/options/button-border-color";
import ButtonBorderWidth from "%/dynamic-component-editor/options/button-border-width";
import IconGap from "%/dynamic-component-editor/options/icon-gap";
import TextAlignment from "%/dynamic-component-editor/options/text-alignment";
import { GestureActionsMixin } from "%/dynamic-component-editor/standard-elements/click-actions/gesture-actions-mixin";

export const SharedButtonComputedStylesMixin = {
  brandingType: ButtonBranding,
  inject: {
    // Provided by `DynamicComponentBuilderElement` so embedded buttons
    // inside a templated component (which mount via DCPE with
    // `edit-mode="false"`) can still recognise they're on the builder
    // canvas and suppress runtime click actions. Outside the builder
    // (staging frame, production mirror, FanWeb) the inject is absent
    // and defaults to `false`, so click actions fire as expected.
    inBuilderEditScope: {
      from: "inBuilderEditScope",
      default: false,
    },
    scopedProvidedContexts: {
      from: "scopedProvidedContexts",
      default: null,
    },
    pageEntities: {
      from: "pageEntities",
      default: null,
    },
  },
  mixins: [GestureActionsMixin],
  computed: {
    buttonVariant() {
      return this.getOptionValue(ButtonType) || "primary";
    },
    defaultTemplateName() {
      return this.hasTextContent || this.editable ? "default" : "hidden";
    },
    iconConfig() {
      return {
        icon: this.getOptionValue(Icon),
        position: this.getOptionValue(IconPosition),
      };
    },
    displayLeftIcon() {
      return Boolean(this.iconConfig.icon) && this.iconConfig.position === "beforeText";
    },
    displayRightIcon() {
      return Boolean(this.iconConfig.icon)
        && (this.iconConfig.position === "afterText" || this.iconConfig.position === "noText");
    },
    displayTopIcon() {
      return Boolean(this.iconConfig.icon) && this.iconConfig.position === "aboveText";
    },
    displayBottomIcon() {
      return Boolean(this.iconConfig.icon) && this.iconConfig.position === "belowText";
    },
    actionType() {
      return this.clickAction?.action;
    },
    myIcon() {
      return this.iconConfig.icon;
    },
    myIconSize() {
      const size = this.getOptionValue(ButtonIconSize) ?? this.fontSizeNumberValue;
      if (typeof size === "string" && size.endsWith("px")) {
        return parseInt(size);
      }
      return size;
    },
    myWidth() {
      return this.getOptionValue(LayoutWidth);
    },
    myHeight() {
      return this.getOptionValue(LayoutHeight);
    },
    myButtonSize() {
      return this.getOptionValue(ButtonSize) || "standard";
    },
    shouldRenderButton() {
      return true;
    },
    clickAction() {
      return this.getOptionValue(ClickAction);
    },
    myPadding() {
      const paddingValues = this.getOptionValue(Padding, BaseOption.$states.DEFAULT, null) ?? {};
      const result = {};
      for (const key of Object.keys(paddingValues)) {
        const val = paddingValues[key];
        result[key] = val === null ? PADDING_DEFAULT_EMPTY_VALUE : val;
      }
      return result;
    },
    myMargins() {
      const marginValues = this.getOptionValue(Margins, BaseOption.$states.DEFAULT, null) ?? {};
      const result = {};
      for (const key of Object.keys(marginValues)) {
        const val = marginValues[key];
        result[key] = val === null ? MARGIN_DEFAULT_EMPTY_VALUE : val;
      }
      return result;
    },
    hasPaddingSet() {
      return Object.values(this.myPadding).every(value => value !== "0px");
    },
    // Legacy publications never registered these options, so the resolver returns
    // `undefined` rather than the declared default — coalesce or every button crashes.
    myIconGap() {
      return this.getOptionValue(IconGap) ?? null;
    },
    // "hug" once the effective (possibly inherited-from-another-breakpoint) gap/alignment
    // is non-default, so untouched buttons don't move but inherited breakpoints still group.
    myContentWidth() {
      const alignment = this.getOptionValue(TextAlignment) ?? "center";
      return this.myIconGap !== null || alignment !== "center" ? "hug" : "fill";
    },
    buttonStyleOverrides() {
      const margins = this.myMargins;
      const padding = this.myPadding;
      const buttonSize = this.myButtonSize;
      const overrides = {
        borderRadius: this.getOptionValue(BorderRadius),
        marginTop: margins.top || "none",
        marginBottom: margins.bottom || "none",
        marginLeft: margins.left || "none",
        marginRight: margins.right || "none",
        boxShadow: this.getOptionValue(BoxShadow),
        boxShadowHover: this.getOptionValue(BoxShadow, BaseOption.$states.HOVER),
        [this.getButtonOverrideKeyForVariant("BackgroundGradient")]: this.getOptionValue(BackgroundGradient),
        [this.getButtonOverrideKeyForVariant("HoverBackgroundGradient")]: this.getOptionValue(BackgroundGradient, BaseOption.$states.HOVER),
        [this.getButtonOverrideKeyForVariant("BorderColor")]: this.getOptionValue(ButtonBorderColor) || "none",
        [this.getButtonOverrideKeyForVariant("BorderWidth")]: this.getOptionValue(ButtonBorderWidth) || "none",
        [this.getButtonOverrideKeyForVariant("HoverBorderColor")]: this.getOptionValue(ButtonBorderColor, BaseOption.$states.HOVER) || "none",
      };

      if (this.hasPaddingSet) {
        overrides[`${buttonSize}PaddingTop`] = padding.top || "none";
        overrides[`${buttonSize}PaddingRight`] = padding.right || "none";
        overrides[`${buttonSize}PaddingBottom`] = padding.bottom || "none";
        overrides[`${buttonSize}PaddingLeft`] = padding.left || "none";
      } else {
        Object.assign(overrides, Padding.buttonSizeOverrides(padding, buttonSize));
      }

      if (this.myIconGap !== null) {
        overrides.contentGap = this.myIconGap;
      }

      const backgroundValue = this.getOptionValue(BackgroundColor);
      const backgroundHoverValue = this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER);

      if (backgroundValue !== "none") {
        overrides[this.getButtonOverrideKeyForVariant("BackgroundColor")] = backgroundValue;
        if (this.getOptionValue(ButtonDisabled)) {
          overrides[this.getButtonOverrideKeyForVariant("DisabledBackgroundColor")] = backgroundValue;
        }
      }

      if (backgroundHoverValue !== "none") {
        overrides[this.getButtonOverrideKeyForVariant("HoverBackgroundColor")] = backgroundHoverValue;
      }

      return overrides;
    },
    clickValue() {
      const action = this.clickAction;
      let urlValue = action?.value;

      if (!action?.linkType) {
        return;
      }

      // The synthesized ClickAction reads `value` from
      // `href-option`'s raw leaf, which is the wrapped
      // `{ source, value }` shape that the inspector's hyperlink
      // picker writes — same as how UrlElement's `computedHref`
      // unwraps before consuming. Skip the unwrap for explicit
      // `hasUpdated: true` writes (the inspector commits a plain
      // string in that path), and for already-absolute URLs.
      if (urlValue && typeof urlValue === "object") {
        urlValue = urlValue.value;
      }

      // Pick the base URL the same way other absolute-path builders do.
      // 1. Server-injected `links.web` — FanWeb sets this to the
      //    publication domain; HomeStudioController sets it to
      //    `/stagingstudio/<dest>` or `/destinationstudio/<dest>` for the
      //    chrome-less Studio render pages.
      // 2. Fallback: derive the studio prefix from the current
      //    `window.location.pathname` when (1) is missing. Without this,
      //    a fresh load of a chrome-less Studio page whose PHP didn't
      //    inject `links.web` (older code path, race during deploy,
      //    redirected route, etc.) would build the click URL as
      //    `${undefined}/about` and the browser would resolve to the
      //    site root instead of the destination's slug space.
      let webBase = this.links?.web ?? "";
      if (!webBase && typeof window !== "undefined") {
        const studioPrefixMatch = window.location.pathname.match(
          /^\/(stagingstudio|destinationstudio)\/([^/?#]+)/,
        );
        if (studioPrefixMatch) {
          webBase = `/${studioPrefixMatch[1]}/${studioPrefixMatch[2]}`;
        }
      }

      if (action.linkType === LinkType.LINK_TYPE_PAGE) {
        urlValue = pageLinkValueFor(action, urlValue, this.scopedProvidedContexts, { quiet: isBuilderQuiet(this) });
        if (urlValue === null) {
          return;
        }
      }

      if (action.linkType === LinkType.LINK_TYPE_PAGE) {
        const quiet = Boolean(typeof this.inBuilderEditScope === "function"
          ? this.inBuilderEditScope()
          : this.inBuilderEditScope);
        const uriVarSources = action.uriVarSources;
        const contextHref = uriVarSources && Object.keys(uriVarSources).length
          ? resolveUriVarsHref(
            uriVarSources, action.contextSource, urlValue, webBase, this.scopedProvidedContexts, this.pageEntities, { quiet },
          )
          : action.contextSource && resolveContextEntityHref(
            action.contextSource, urlValue, webBase, this.scopedProvidedContexts, this.pageEntities, { quiet },
          );
        if (contextHref) {
          return contextHref;
        }
      }

      switch (action.linkType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE: {
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          const pageHref = `${webBase}/${urlValue}`;
          return hasUnfilledPlaceholder(pageHref) ? undefined : pageHref;
        }
        case LinkType.LINK_TYPE_STORY:
          return `${webBase}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${webBase}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            linkType: action.linkType,
            urlValue,
          });
      }
    },
    linkType() {
      return this.clickAction?.linkType;
    },
    openInNewTab() {
      return this.clickAction?.openInNewTab;
    },
  },
  methods: {
    setDefaultBrandingValue() {
      // override text element mixin method to avoid adding text branding store
    },
    getButtonOverrideKeyForVariant(overrideKey) {
      return `${this.buttonVariant}Variant${overrideKey}`;
    },
    handleButtonClick(e) {
      // A click that is authored as a gesture belongs to the gesture engine,
      // which runs the same action: doing it here as well navigates twice, or
      // opens two tabs.
      if (this.gestureOwnsTheClick) {
        return;
      }
      // In the builder canvas, the only meaningful click on a button is
      // "select / drag this element". Configured actions (goToUrl,
      // custom handlers) belong to runtime — firing them in editMode
      // navigates the builder away mid-edit. Swallow the click here so
      // canvas selection still bubbles but the action does not run.
      // `inBuilderEditScope` extends the same gate to buttons rendered
      // INSIDE a templated component on the builder canvas — those
      // mount via DCPE with `edit-mode="false"` and would otherwise
      // fire the action.
      if (suppressesNavigation(this)) {
        return;
      }
      if (this.handleCustomClick) {
        this.handleCustomClick(e);
        return;
      }

      if (this.actionType === "goToUrl") {
        if (!this.clickValue) {
          return;
        }

        const a = document.createElement("a");
        if (this.openInNewTab) {
          a.target = "_blank";
        }
        a.href = this.clickValue;
        a.click();
      } else {
        // ignore for now
      }
    },
  },
};
